'use client';

import React from 'react';
import styled from 'styled-components';
import { PlayIcon, PauseIcon, EditIcon, DeleteIcon } from '@/components/icons/MonitorIcons';
import { StatusBadge } from '@/components/ui-components/StatusBadge';

interface MonitorHeaderProps {
  title: string;
  description: string;
  status: 'UP' | 'DOWN' | 'PAUSED';
  onTestNotification?: () => void;
  onPause?: () => void;
  onEdit?: () => void;
  onMenu?: () => void;
  className?: string;
}

const Container = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px 32px;
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 32px;
`;

const StatusSection = styled.div`
  display: flex;
  align-items: center;
`;

const InfoSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ExternalLinkIcon = styled.svg`
  width: 20px;
  height: 20px;
  color: #9ca3af;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #ffffff;
  }
`;

const Description = styled.div`
  font-size: 14px;
  color: #9ca3af;
  
  a {
    color: #60a5fa;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ActionsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconButton = styled.button`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  color: #374151;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    border-color: #d1d5db;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const MenuButton = styled.button`
  background: #ffffff;
  border: 1px solid #ef4444;
  color: #ef4444;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;

  &:hover {
    background: #ef4444;
    color: #ffffff;
    border-color: #ef4444;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

/**
 * MonitorHeader Component
 * 
 * Header section for monitor detail page with status, title, and actions
 */
export const MonitorHeader: React.FC<MonitorHeaderProps> = ({
  title,
  description,
  status,
  onPause,
  onEdit,
  onMenu,
  className,
}) => {
  return (
    <Container className={className}>
      <StatusSection>
        <StatusBadge status={status} size="lg" />
      </StatusSection>

      <InfoSection>
        <Title>
          {title}
          <ExternalLinkIcon viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </ExternalLinkIcon>
        </Title>
        <Description dangerouslySetInnerHTML={{ __html: description }} />
      </InfoSection>

      <ActionsSection>
        <IconButton onClick={onPause}>
          {status === 'PAUSED' ? (
            <>
              <PlayIcon />
              Start
            </>
          ) : (
            <>
              <PauseIcon />
              Pause
            </>
          )}
        </IconButton>

        <IconButton onClick={onEdit}>
          <EditIcon />
          Edit
        </IconButton>

        <MenuButton onClick={onMenu}>
          <DeleteIcon />
        </MenuButton>
      </ActionsSection>
    </Container>
  );
};

export default MonitorHeader;

